/* EL BUZÓN DE AVISOS, ANTES DE PINTAR NADA. Guarda el aviso del arranque en
   frío que el envoltorio nativo entrega de un solo disparo, para que no se
   pierda mientras React se monta. Ojo con lo que esto NO es: los `import` se
   izan, así que esta llamada NO corre antes que el resto del módulo — corre
   antes de `render()`, que es lo que hace falta aquí. El pronto de verdad lo
   pone el `<script>` del `index.html`, que corre mientras se lee el HTML; esto
   es la red por si ese script desaparece de ahí algún día. Ver
   `src/lib/avisoPendiente.ts`. */
import { instalarBuzonDeAvisos } from "@/lib/avisoPendiente";
instalarBuzonDeAvisos();

/* EL MODO SIN CONEXIÓN, TAMBIÉN ANTES DE PINTAR: la red que contesta desde el
   teléfono cuando no hay cobertura y el usuario cuyos datos se leen. Tiene que
   estar puesto antes de que la primera pantalla pida nada. Ver
   `src/lib/offline/arranqueOffline.ts`. */
import { arrancarOffline } from "@/lib/offline/arranqueOffline";
arrancarOffline();

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { isDespiaAndroid } from "@/lib/isDespiaNative";
import { installAndroidInsetWatcher } from "@/lib/androidInsets";

// Import i18n to trigger synchronous initialization
import "@/i18n/config";

// Mark the Android native (Despia) WebView so CSS can reserve extra bottom
// clearance (its env(safe-area-inset-bottom) reports 0 despite the on-screen
// system nav bar). Done before render, outside React, so there is no flash.
if (isDespiaAndroid()) {
  document.documentElement.classList.add("despia-android");
}

// Measure the REAL system nav bar inset (3-button vs gesture navigation) and
// write `--android-nav-inset` in px. Runs here, synchronously and via a static
// import, so the FIRST paint already uses the measured value: with a dynamic
// import the dock would paint with the 48px fallback and then jump. No-op
// outside `html.despia-android` (iOS, mobile web and desktop).
installAndroidInsetWatcher();

/* EL TROZO QUE NO LLEGÓ, Y POR QUÉ LA APP «A VECES SÍ Y A VECES NO».
   (08-09-2026, reproducido: 4 de 24 arranques con la red cortándose a mitad.)

   La app se descarga a trozos, y los trozos se piden cuando hacen falta. Si la
   cobertura se va justo en ese segundo, la petición del trozo falla — y el
   navegador SE ACUERDA del fallo: ese `import()` queda rechazado para siempre y
   no se vuelve a pedir nunca, aunque la cobertura vuelva un segundo después.
   React no puede montar sin ese trozo, así que la pantalla se queda en blanco y
   ya no sale de ahí. Es exactamente el fallo del que se sale «apagando y
   encendiendo»: lo único que lo cura es volver a cargar.

   Vite avisa de esto con `vite:preloadError`. Recogerlo y recargar UNA vez
   convierte un bloqueo definitivo en un parpadeo. La llave es la misma que usa el
   vigía del `index.html`, así que entre los dos hay como mucho UNA recarga
   automática por pestaña: nadie va a ver la app recargándose en bucle. Y se
   comprueba que el apunte se ha guardado de verdad antes de recargar, porque en
   navegación privada `sessionStorage` no guarda y eso sí sería un bucle. */
window.addEventListener("vite:preloadError", (evento) => {
  evento.preventDefault();
  const LLAVE_REINTENTO = "ulture-reintento-automatico";
  try {
    if (sessionStorage.getItem(LLAVE_REINTENTO)) return;
    sessionStorage.setItem(LLAVE_REINTENTO, "1");
    if (sessionStorage.getItem(LLAVE_REINTENTO) !== "1") return;
  } catch {
    return;
  }
  window.location.reload();
});

// Stop the browser from restoring scroll on back/forward — we handle it
// ourselves in layouts so route changes always land at the top.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  try {
    window.history.scrollRestoration = "manual";
  } catch {
    /* noop */
  }
}


// Global: dismiss keyboard when tapping outside input fields (mobile UX).
// IMPORTANT: do NOT blur when the touch target is itself a focusable/interactive
// element (another input, button, select, link, etc.). Otherwise the OS keyboard
// closes and re-opens between fields, producing a visible glitch and making the
// next tap feel "lost". This matches Revolut / iOS Mail / Stripe behavior:
// focus transfers between fields without dismissing the keyboard, and only a
// tap on truly inert background closes it.
const FOCUSABLE_SELECTOR =
  'input, textarea, select, button, a[href], [contenteditable="true"], [tabindex]:not([tabindex="-1"]), [role="button"], [role="combobox"], [role="textbox"], [role="link"], [role="menuitem"], [role="option"], [role="tab"], [role="switch"], [role="checkbox"], [role="radio"], label';

document.addEventListener("touchstart", (e) => {
  const target = e.target as HTMLElement | null;
  const active = document.activeElement as HTMLElement | null;
  if (!target || !active) return;
  const isEditable =
    active.tagName === "INPUT" ||
    active.tagName === "TEXTAREA" ||
    active.isContentEditable;
  if (!isEditable) return;
  if (target === active || active.contains(target)) return;
  // If the tap is on another interactive/focusable element, let the browser
  // transfer focus naturally. Do not blur — keyboard stays up.
  if (target.closest(FOCUSABLE_SELECTOR)) return;
  active.blur();
}, { passive: true });

// El HTML para buscadores que mete `scripts/seo-prerender.ts` en cada ruta
// pública. Es para robots que no ejecutan JavaScript; aquí ya lo ejecutamos,
// así que fuera antes de pintar (la pantalla de relevo lo tapaba hasta ahora).
document.getElementById("seo-fallback")?.remove();

const root = createRoot(document.getElementById("root")!);
root.render(
  <StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </StrictMode>
);
